import { useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { useFieldName, useSchemaForm } from "./context";
import { buildDefault } from "./defaults";
import { FieldNode } from "./types";

/*
 * State of a field that can be switched off (value null) and on again.
 * Switching off keeps the value aside; switching on restores it or builds a default.
 */
export const useNullableField = (node: FieldNode) => {
  const { valueCache } = useSchemaForm();
  const { control, setValue } = useFormContext();
  const name = useFieldName(node.path);
  const value = useWatch({ control, name });
  const enabled = value !== null && value !== undefined;

  const set = useCallback(
    (next: unknown) => setValue(name, next, { shouldDirty: true }),
    [name, setValue],
  );

  const toggle = useCallback(
    (on: boolean) => {
      if (!on) {
        valueCache.current[name] = value;
        set(null);
        return;
      }

      set(valueCache.current[name] ?? buildDefault(node, false));
    },
    [name, node, set, value, valueCache],
  );

  return { name, value, enabled, set, toggle };
};
